import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing reset token in the URL");
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPassword !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      await refresh();
      setDone(true);
      toast.success("Password reset — you're logged in");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone2-50" data-testid="reset-invalid">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-14">
          <div className="paper-card p-6">
            <h1 className="font-heading font-bold text-sage-900 text-xl">Reset link is missing</h1>
            <p className="text-sm text-stone2-600 mt-2">
              This page needs a reset token. Please use the link from your reset email
              or request a new one below.
            </p>
            <Link to="/forgot-password" className="btn-primary w-full justify-center mt-4">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="reset-password-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-14">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold mb-4 hover:text-sage-900">
          <ArrowLeft className="w-4 h-4" /> Back to log in
        </Link>
        <div className="paper-card p-6 sm:p-8">
          <h1 className="font-heading font-bold text-sage-900 text-2xl">Choose a new password</h1>
          <p className="text-sm text-stone2-600 mt-1">
            At least 8 characters, with letters and numbers.
          </p>

          {done ? (
            <div className="mt-6 rounded-xl bg-sage-50 border border-sage-200 p-4 text-sm inline-flex items-center gap-2" data-testid="reset-done-state">
              <CheckCircle2 className="w-4 h-4 text-sage-700" />
              Password reset. Redirecting…
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="field-label" htmlFor="np">New password</label>
                <input id="np" type="password" required minLength={8}
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-new-password" autoComplete="new-password"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
              <div>
                <label className="field-label" htmlFor="cp">Confirm new password</label>
                <input id="cp" type="password" required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password" autoComplete="new-password"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-primary w-full justify-center">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
